/** Browser analytics is suspended throughout CMS views; server analytics is separate. */
import { createPrivacyAmplitude, isCms, type AmplitudePageProps } from './amplitude-privacy';
export type { AmplitudePageProps } from './amplitude-privacy';
let bridge: ReturnType<typeof createPrivacyAmplitude> | undefined;
export function analyticsAllowed(tracking: AmplitudePageProps['tracking']): boolean {
    return !tracking?.cookieConsent?.enabled || Boolean(tracking.consent?.given && tracking.consent.categories?.analytics);
}
function suspendSafely(): void {
    try {
        bridge?.suspend();
    } catch {
        /* Analytics must never interrupt navigation. */
    }
}
function getBridge() {
    try {
        if (typeof window === 'undefined') return;
        const key = (import.meta.env.VITE_AMPLITUDE_API_KEY as string | undefined) || '';
        if (key && !bridge) bridge = createPrivacyAmplitude(key);
        return bridge;
    } catch {
        suspendSafely();
        return undefined;
    }
}
type HeadRegistration = { token: string; view: string; props: AmplitudePageProps | undefined };
let acceptedHead: HeadRegistration | undefined;
let processingHead: HeadRegistration | undefined;
let head: HeadRegistration | undefined;
let pending: { view: string; props: AmplitudePageProps | undefined } | undefined;
let observer: MutationObserver | undefined;
function viewKey(url: string): string {
    const parsed = new URL(url, location.origin);
    return parsed.origin + parsed.pathname + parsed.search;
}
function disconnectHead(): void {
    observer?.disconnect();
    observer = undefined;
}
function flushHead(): void {
    if (!head || !pending || head.props !== pending.props || head.view !== pending.view || head.view !== viewKey(location.href)) return;
    if (document.head.querySelector('meta[name="lrt-amplitude-view"]')?.getAttribute('content') !== head.token) return;
    const accepted = pending;
    const owner = head;
    processingHead = owner;
    pending = undefined;
    disconnectHead();
    try {
        const active = getBridge();
        const task = active?.afterPage(accepted.props);
        if (!task) {
            processingHead = undefined;
            return;
        }
        void task
            .then(() => {
                if (head === owner && processingHead === owner && active && !active.isBlocked()) acceptedHead = owner;
            })
            .catch(() => {
                if (head === owner && processingHead === owner) suspendSafely();
            })
            .finally(() => {
                if (processingHead === owner) processingHead = undefined;
            });
    } catch {
        if (processingHead === owner) processingHead = undefined;
        suspendSafely();
    }
}
function observeHead(): void {
    disconnectHead();
    if (!head || !pending || isCms(location.href)) return;
    observer = new MutationObserver(() => {
        try {
            flushHead();
        } catch {
            suspendSafely();
        }
    });
    observer.observe(document.head, { childList: true, subtree: true, attributes: true });
    flushHead();
}
/** The marker is committed in the same Inertia head batch as the public title. */
export function announceAmplitudeHead(token: string, url: string, props: AmplitudePageProps | undefined): () => void {
    try {
        const registration = { token, view: viewKey(url), props };
        head = registration;
        if (registration.view === viewKey(location.href)) bootAmplitude(props);
        return () => {
            if (head !== registration) return;
            head = undefined;
            acceptedHead = undefined;
            processingHead = undefined;
            disconnectHead();
            suspendSafely();
        };
    } catch {
        suspendSafely();
        return () => {};
    }
}
function stopForNavigation(): void {
    pending = undefined;
    acceptedHead = undefined;
    processingHead = undefined;
    head = undefined;
    disconnectHead();
    suspendSafely();
}
// Native history bypasses Inertia beforeUpdate; suspend before its page swap.
if (typeof window !== 'undefined') window.addEventListener('popstate', stopForNavigation, true);
export function prepareAmplitudeNavigation(url: string, props: AmplitudePageProps | undefined): void {
    try {
        getBridge()?.beforePage(url, props);
        stopForNavigation();
    } catch {
        suspendSafely();
    }
}
export function bootAmplitude(props: AmplitudePageProps | undefined): void {
    try {
        if (
            head &&
            (processingHead === head || (acceptedHead === head && bridge && !bridge.isBlocked())) &&
            head.props === props &&
            head.view === viewKey(location.href) &&
            !isCms(location.href) &&
            analyticsAllowed(props?.tracking)
        )
            return;
        const current = getBridge();
        current?.suspend();
        if (isCms(location.href) || !analyticsAllowed(props?.tracking)) {
            stopForNavigation();
            void current?.afterPage(props).catch(suspendSafely);
            return;
        }
        pending = { view: viewKey(location.href), props };
        observeHead();
    } catch {
        suspendSafely();
    }
}
export function trackAmplitude(event: string, properties?: Record<string, unknown>): void {
    try {
        if (bridge && !bridge.isBlocked()) bridge.client.track(event, properties);
    } catch {
        suspendSafely();
    }
}
